// src/components/StateCards.tsx
//
// The five designed states the build brief requires everywhere data is shown:
// loading, empty, error, no-permission, not-built-yet. Each is its own
// component so a page picks exactly one rather than improvising a sixth. None
// of them ever vanishes silently — "a card that vanishes is indistinguishable
// from one that broke" (ux-direction §4.1).
import { plainProse } from '../lib/plain-words'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { SgdsButton, SgdsSkeleton } from '@govtechsg/sgds-web-component/react'

export function LoadingCard({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    /*
      ⚠ `role="status"` as well as `aria-busy`. `aria-busy` marks the region as
      in flux; it does not ANNOUNCE anything. Without a live region the sr-only
      label below is read only if the user happens to navigate onto it, so a
      screen-reader user got silence where a sighted user saw a skeleton — and
      `ux-direction` §4.1 asks for both halves.
    */
    <div className="pt-card" role="status" aria-busy="true" style={{ boxShadow: 'none' }}>
      <span className="sr-only">{label}</span>
      <SgdsSkeleton height="24px" width="60%" borderRadius="4px" />
      <div className="mt-4">
        <SgdsSkeleton height="40px" width="40%" borderRadius="6px" />
      </div>
      <div className="mt-4">
        <SgdsSkeleton height={`${rows * 22}px`} rows={rows} borderRadius="4px" />
      </div>
    </div>
  )
}

export function ErrorCard({
  title,
  message,
  reference,
  onRetry,
}: {
  title: string
  message: string
  reference?: string
  onRetry?: () => void
}) {
  return (
    <div className="pt-card" role="alert">
      <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">{title}</p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{plainProse(message)}</p>
      {reference !== undefined && (
        <p className="mt-2 text-sm text-[var(--sgds-color-subtle)]">
          Reference: <span className="pt-mono">{reference}</span>
        </p>
      )}
      {onRetry !== undefined && (
        <div className="mt-4">
          <SgdsButton variant="outline" onClick={onRetry} type="button">
            Retry
          </SgdsButton>
        </div>
      )}
    </div>
  )
}

export function EmptyCard({
  title,
  message,
  goodNews = false,
  onClear,
}: {
  title: string
  message: string
  /** True when an empty result is the desired outcome (e.g. "nothing awaiting action"). */
  goodNews?: boolean
  onClear?: () => void
}) {
  return (
    <div className="pt-card text-center py-10">
      <p className={goodNews ? 'font-semibold text-[var(--sgds-success-color-emphasis)]' : 'font-semibold'}>
        {title}
      </p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)] max-w-[60ch] mx-auto">{plainProse(message)}</p>
      {onClear !== undefined && (
        <div className="mt-4">
          <SgdsButton variant="outline" onClick={onClear} type="button">
            Clear filters
          </SgdsButton>
        </div>
      )}
    </div>
  )
}

export function NoPermissionCard({ requiredRole }: { requiredRole: string }) {
  return (
    <div className="pt-card">
      <p className="font-semibold">You do not have access to this.</p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
        Required role: <strong>{requiredRole}</strong>. Your active role does not carry it.
      </p>
      {/*
        ★★★ WAVE 24 — A REFUSAL SHIPS WITH THE WAY OUT, IN THE SAME CARD.

        This was a permanently `disabled` "Request access" button — the only
        control in the product that could never become enabled — carrying its
        explanation in a `title` attribute, which is invisible on touch, invisible
        to most screen readers, and appears only after a pointer hover the officer
        has no reason to attempt.

        So the screen refused, and then said nothing an officer could act on. That
        is the shape this estate already names: a refusal with no alternative is
        the same failure wearing a better comment. The question to answer is
        "what does the blocked user do NOW?", and it has a real answer here —
        this product does not grant access at all. Account and role changes are
        CAM's (ADR 0011: Annex A 52-59 are read-only here, the writes stay CAM's),
        and the route that exists in THIS product for asking a human is the
        support ticket. Both are named, and the button that could not work is gone.
      */}
      <p className="mt-4 text-[var(--sgds-body-color-subtle)]">
        Roles are granted in <strong>CAM</strong>, not here: this product reads the access it is given and
        cannot change it. Ask your agency&rsquo;s CAM administrator for the role above. If you are not sure who that
        is, <Link to="/support-tickets">raise a support ticket</Link> and say which screen refused you.
      </p>
    </div>
  )
}

/**
 * ★★★ WAVE 27 (owner-directed, 2026-08-27) — WAS `UnbuiltCard`, AND ITS CLOSING
 * LINE WAS *"Not built yet. No action will make data appear here."*
 *
 * ## Why the wording changed, and what did NOT change with it
 *
 * The old line was accurate and it read as a dead end. To an evaluator walking
 * this product it says *we did not do this*, full stop — when the true statement
 * in every one of the four cases is *this waits on something nameable, and we
 * build it when that arrives*. The owner's instruction was to say the second
 * thing: **name what has to happen or be provided, and say we build on it then.**
 *
 * ⚠ **THE HONESTY GUARANTEE IS UNCHANGED AND IS THE REASON THIS COMPONENT EXISTS.**
 * A surface with no data still announces itself, so it cannot be mistaken for a
 * broken one, and it still shows **no figure** rather than a placeholder — that
 * sentence is on the card, in its own line, and it is fixed. What changed is the
 * frame around it, not the claim inside it. Nothing here may imply a number is
 * coming when none is (`ENGINEERING.md`, and the reason `DashboardPage.tsx`'s
 * cards exist at all rather than inventing a metric).
 *
 * ⚠ **`availableWhen` IS REQUIRED, and that is the whole design.** A card that
 * could render without naming its precondition would drift straight back to
 * "not built yet" with extra steps — the same argument that made {@link BuiltCard}
 * a separate component rather than a boolean prop. If you cannot state the
 * condition, you do not yet understand why the surface is empty, and the card is
 * the wrong tool.
 *
 * ⚠ **The class stays `.pt-unbuilt`.** It is a shared inset-panel STYLE used by
 * six other surfaces that are not status cards at all (`BillDetailPage`,
 * `AuditArchivePage`, `WardRulesPage`, `ReprorationConsolePage`), so renaming it
 * would touch screens this change has no business touching. It is not
 * customer-visible.
 */
export function AwaitingCard({
  title,
  note,
  availableWhen,
}: {
  title: string
  note: ReactNode
  availableWhen: ReactNode
}) {
  return (
    <div className="pt-unbuilt">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">{title}</p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{note}</p>
      <p className="mt-2 font-semibold">Ready to build once {availableWhen}.</p>
      <p className="mt-1 text-xs text-[var(--sgds-color-subtle)]">
        Until then this surface shows no figure, rather than a placeholder.
      </p>
    </div>
  )
}

/**
 * ★★ WAVE 9 — the counterpart to `AwaitingCard`, for a surface that IS built.
 *
 * ## Why a second card and not a prop on the first
 *
 * `AwaitingCard`'s closing line is fixed — it names a precondition and states
 * that no figure is shown until then — and `ReferenceDataPage` printed the old
 * form of it, *"Not built yet…"*, over EVERY parameter
 * note, including the notes that said the parameter is read at
 * `POST /api/bills/:reference/compute`. Eight of the ten pro-ration parameters
 * are read today, so the card was contradicting its own body text on the eight
 * that carry the money. ENGINEERING.md's discipline is that an unbuilt surface must
 * announce it is unbuilt; a BUILT surface announcing that it is not is the same
 * defect inverted, and it costs more, because it invites an officer to discount
 * every other honesty notice in the product.
 *
 * A boolean prop on `AwaitingCard` would have left a component called *awaiting*
 * rendering "in force", which is how the `not_built_note` column came to hold
 * sentences saying the opposite of its own name (`024_reference_model_note.sql`).
 * Two cards, two names, and the caller picks by the status the SERVER published.
 *
 * `caveat` is for the one honest middle state — a series with one axis read and
 * another named axis not — and is rendered as its own line rather than folded
 * into `note`, so it cannot be missed at a glance.
 */
export function BuiltCard({ title, note, caveat }: { title: string; note: ReactNode; caveat?: string }) {
  return (
    <div className="pt-built">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">{title}</p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{note}</p>
      <p className="mt-2 font-semibold">
        {caveat === undefined ? 'Built and in force. This value is read when a bill is computed.' : caveat}
      </p>
    </div>
  )
}
